// GrillSizesTab.js - Grill sizes configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  InfoBox
} from './GrillsCommon';
import { useProductScope } from '../utils/productScope';

const GrillSizesTab = () => {
  const [rows, setRows] = useState([]);
  const [grillTypes, setGrillTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filterModel, setFilterModel] = useState({ items: [] });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    Promise.all([authFetch('/grills/sizes'), authFetch('/grills/types')])
      .then(([sizes, types]) => { setRows(sizes); setGrillTypes(types); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      const payload = { ...formData, fkGrillType: formData.fkGrillType || null };
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/grills/sizes/${editData.id}`, payload, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/grills/sizes', payload, 'POST');
        setRows(prevRows => [...prevRows, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this grill size?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/grills/sizes/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const getTypeName = (fkGrillType) => {
    if (!fkGrillType) return '(All Types)';
    const type = grillTypes.find(t => t.id === fkGrillType);
    return type?.label || fkGrillType;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'fkGrillType', headerName: 'Grill Type', width: 150,
      valueGetter: (value) => getTypeName(value),
      renderCell: (params) => getTypeName(params.row.fkGrillType) },
    { field: 'label', headerName: 'Label', width: 150 },
    { field: 'value', headerName: 'Value', width: 150 },
    { field: 'applies_to', headerName: 'Applies To', width: 120 },
    { field: 'active', headerName: 'Active', width: 100,
      valueGetter: (value) => value ? 'Yes' : 'No',
      renderCell: (params) => params.row.active ? 'Yes' : 'No' },
    { field: 'sort_order', headerName: 'Sort Order', width: 100 }
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Size
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Size
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedRow}>
          Delete Size
        </Button>
      </Stack>

      <InfoBox />

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
        />
      )}

      <GrillSizeDialog
        open={openDialog}
        data={editData}
        grillTypes={grillTypes}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const GrillSizeDialog = ({ open, data, grillTypes, onClose, onSave }) => {
  const { defaultScope, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({ fkGrillType: '', label: '', value: '', applies_to: 'windows', active: true, sort_order: 0 });

  useEffect(() => {
    if (data) {
      setFormData({ fkGrillType: data.fkGrillType || '', label: data.label || '', value: data.value || '', applies_to: data.applies_to || defaultScope, active: data.active ?? true, sort_order: data.sort_order || 0 });
    } else {
      setFormData({ fkGrillType: '', label: '', value: '', applies_to: defaultScope, active: true, sort_order: 0 });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Grill Size' : 'Add Grill Size'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Grill Type (leave empty for all)</InputLabel>
            <Select value={formData.fkGrillType} onChange={(e) => setFormData({ ...formData, fkGrillType: e.target.value })} label="Grill Type (leave empty for all)">
              <MenuItem value="">(All Types)</MenuItem>
              {grillTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth />
          <TextField label="Value" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Applies To</InputLabel>
            <Select
              value={formData.applies_to}
              label="Applies To"
              onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
            >
              {optionsForValue(formData.applies_to).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} fullWidth />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrillSizesTab;
